import { FormControl } from "@mui/material";
import { DateTime } from "luxon";
import { ReactElement } from "react";
import { Controller } from "react-hook-form";
import type { Control, FieldPath, FieldValues } from "react-hook-form";

import { DatePickerWithError, DatePickerWithErrorProps } from "@/components/inputsWithError";

export interface FormControlDatePickerProps<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
  TContext = unknown,
> extends DatePickerWithErrorProps {
  name: TName;
  control: Control<TFieldValues, TContext>;
  errorMsg?: string;
  sx?: import("@mui/system").SxProps<import("@mui/material/styles").Theme>;
  InputLabelProps?: object;
  "data-cy"?: string;
}

export const FormControlDatePicker = <
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
  TContext = unknown,
>({
  name,
  control,
  errorMsg,
  sx,
  InputLabelProps,
  "data-cy": dataCy,
  ...props
}: FormControlDatePickerProps<TFieldValues, TName, TContext>): ReactElement => (
  <FormControl error={Boolean(errorMsg)} sx={sx} data-cy={dataCy}>
    <Controller
      control={control}
      name={name}
      render={({ field }) => {
        let safeValue: DateTime | null = null;

        if (field.value) {
          // For string ISO dates
          if (typeof field.value === "string" && field.value !== "") {
            try {
              // Ensure UTC parsing
              const parsedDate = DateTime.fromISO(field.value, { zone: "utc" });
              safeValue = parsedDate.isValid ? parsedDate : null;
            } catch {
              safeValue = null;
            }
          } else if (typeof field.value === "object") {
            safeValue = field.value;
          }
        }

        return (
          <DatePickerWithError
            value={safeValue}
            onChange={(newValue: DateTime | null) => {
              // Convert to UTC ISO string when saving
              if (newValue) {
                const utcValue = newValue.toUTC().toISO();
                field.onChange(utcValue);
              } else {
                field.onChange(null);
              }
            }}
            errorMsg={errorMsg}
            slotProps={{
              textField: {
                InputLabelProps: {
                  shrink: true,
                  ...InputLabelProps,
                },
              },
            }}
            {...props}
          />
        );
      }}
    />
  </FormControl>
);
